import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Eye, EyeOff, Lock, User, AlertTriangle, Shield, BarChart3, FileText,
  Headphones, ChevronDown, ChevronUp, Send, Phone, Mail, MapPin,
  Plane, Radio, Clock, CheckCircle2, ArrowRight, Menu, X, Activity, Film,
  MessageCircle, Bot, Loader2, Minimize2, Mic, MicOff, Volume2, Square
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const VIDEO_URL = "https://customer-assets.emergentagent.com/job_b5cb1511-d064-4852-992c-1923642a6a2e/artifacts/a9e22iqs_k.jbkln..mp4";

// Animated counter hook
const useCounter = (end, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start);
  const ref = useRef(null);
  const counted = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !counted.current) {
          counted.current = true;
          const startTime = Date.now();
          const tick = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(start + (end - start) * eased));
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration, start]);

  return { count, ref };
};

// FAQ Accordion Item
const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden transition-all hover:border-[#C8941A]/40" data-testid="faq-item">
    <button onClick={onToggle} className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors">
      <span className="font-semibold text-[#0A1628] text-sm pr-4">{question}</span>
      {isOpen ? <ChevronUp size={18} className="text-[#C8941A] flex-shrink-0" /> : <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />}
    </button>
    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
      <p className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{answer}</p>
    </div>
  </div>
);

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showLogin, setShowLogin] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [contactForm, setContactForm] = useState({ nome: '', email: '', empresa: '', mensagem: '' });
  const [contactSent, setContactSent] = useState(false);
  // Sales chatbot state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId] = useState(() => crypto.randomUUID());
  const [isRecording, setIsRecording] = useState(false);
  const [chatTtsLoading, setChatTtsLoading] = useState(null);
  const [chatPlaying, setChatPlaying] = useState(null);
  const [autoVoice, setAutoVoice] = useState(true);
  const chatScrollRef = useRef(null);
  const mediaRecRef = useRef(null);
  const chatAudioRef = useRef(null);

  const { login } = useAuth();
  const navigate = useNavigate();

  // Auto-scroll chat
  useEffect(() => {
    if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [chatMessages, chatLoading]);

  // Welcome message
  useEffect(() => {
    if (chatOpen && chatMessages.length === 0) {
      setChatMessages([{ role: 'assistant', content: 'Ola! Sou o **Jorginho**, consultor senior em projetos RPAS. Me conta: como funciona a operacao de drones na sua empresa hoje? Quero entender onde posso te ajudar de verdade.' }]);
    }
  }, [chatOpen]);

  const sendChatMessage = async (text) => {
    const msg = (text || chatInput).trim();
    if (!msg || chatLoading) return;
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: msg }]);
    setChatLoading(true);
    try {
      const r = await axios.post('/api/ai/sales-chat', { message: msg, session_id: chatSessionId });
      const aiResponse = r.data.response;
      setChatMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
      // Auto-speak response
      if (autoVoice && aiResponse) {
        const newIdx = chatMessages.length + 1;
        setTimeout(() => speakResponse(aiResponse, newIdx), 300);
      }
    } catch {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Desculpe, tive um problema. Entre em contato pelo WhatsApp: **(71) 98355-9776**' }]);
    } finally { setChatLoading(false); }
  };

  const speakResponse = async (text, msgIdx) => {
    if (chatAudioRef.current) { chatAudioRef.current.pause(); chatAudioRef.current = null; }
    if (chatPlaying === msgIdx) { setChatPlaying(null); return; }
    const clean = text.replace(/[#*_`\[\]()>]/g, '').replace(/\n+/g, '. ').trim();
    if (!clean) return;
    setChatTtsLoading(msgIdx);
    try {
      const r = await axios.post('/api/ai/sales-tts', { text: clean });
      const audio = new Audio(`data:audio/mp3;base64,${r.data.audio_base64}`);
      chatAudioRef.current = audio;
      setChatPlaying(msgIdx);
      setChatTtsLoading(null);
      audio.play();
      audio.onended = () => { setChatPlaying(null); chatAudioRef.current = null; };
      audio.onerror = () => { setChatPlaying(null); chatAudioRef.current = null; };
    } catch { setChatTtsLoading(null); }
  };

  const toggleMic = async () => {
    if (isRecording) {
      mediaRecRef.current?.stop();
      setIsRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('audio', blob, 'voice.webm');
        try {
          const r = await axios.post('/api/ai/sales-stt', formData);
          if (r.data.text) {
            setChatInput(r.data.text);
            sendChatMessage(r.data.text);
          }
        } catch { toast.error('Erro ao processar voz'); }
      };
      recorder.start();
      mediaRecRef.current = recorder;
      setIsRecording(true);
    } catch { toast.error('Microfone nao disponivel'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
      toast.success('Login realizado com sucesso!');
      setShowLogin(false);
      navigate('/dashboard');
    } catch (err) {
      const message = err.response?.data?.detail || (err.code === 'ERR_NETWORK' ? 'Servidor indisponivel.' : 'Erro ao realizar login');
      setError(message);
    } finally { setLoading(false); }
  };

  const handleContact = (e) => {
    e.preventDefault();
    setContactSent(true);
    toast.success('Mensagem enviada com sucesso!');
    setTimeout(() => setContactSent(false), 5000);
    setContactForm({ nome: '', email: '', empresa: '', mensagem: '' });
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenu(false);
  };

  // Counters
  const missions = useCounter(12450);
  const hours = useCounter(8730);
  const drones = useCounter(156);
  const clients = useCounter(42);

  const features = [
    { icon: Radio, title: 'Monitoramento em Tempo Real', desc: 'Acompanhe todas as operacoes RPAS com telemetria ao vivo, status de drones e alertas instantaneos.', color: 'from-blue-500 to-cyan-500' },
    { icon: FileText, title: 'Relatorios Automatizados', desc: 'Gere relatorios de jornada em PDF, Excel e Word com graficos, KPIs e analises detalhadas.', color: 'from-[#C8941A] to-amber-500' },
    { icon: Shield, title: 'Compliance ANAC/DECEA', desc: 'Controle de documentacao, licencas e conformidade regulatoria com alertas de vencimento.', color: 'from-emerald-500 to-green-500' },
    { icon: Headphones, title: 'Assistente IA Integrado', desc: 'Jorginho, o assistente IA, analisa dados, gera relatorios e executa acoes por comando de voz ou texto.', color: 'from-purple-500 to-indigo-500' },
  ];

  const services = [
    { title: 'Monitoramento', icon: Activity, items: ['Telemetria em tempo real', 'Status de drones ao vivo', 'Alertas de anomalias', 'Rastreamento GPS'] },
    { title: 'Transmissao ao Vivo', icon: Radio, items: ['Livestreaming com ultra baixa latencia', 'Video HD direto do drone', 'Multiplas cameras simultaneas', 'Compartilhamento por link seguro'] },
    { title: 'Armazenamento em Nuvem', icon: Film, items: ['Videos e fotos em nuvem ilimitados', 'Upload ultra-rapido sem limite de tamanho', 'Geotagging GPS automatico', 'Organizacao por pastas e projetos'] },
    { title: 'Relatorios', icon: BarChart3, items: ['Jornada diaria e mensal', 'KPIs operacionais', 'Exportacao PDF/Excel/Word', 'Graficos automaticos'] },
    { title: 'Compliance', icon: Shield, items: ['Documentacao ANAC', 'Controle de seguros', 'Alertas de vencimento', 'Auditoria de operacoes'] },
    { title: 'Suporte', icon: Headphones, items: ['Assistente IA Jorginho', 'Suporte tecnico 24/7', 'Treinamento operacional', 'Base de conhecimento'] },
  ];

  const faqs = [
    { q: 'O que e a plataforma Gestao de RPAS?', a: 'E uma plataforma completa de gestao e monitoramento de operacoes com aeronaves remotamente pilotadas (drones). Oferece telemetria em tempo real, registro de jornada, gestao de equipe, compliance regulatorio e assistente IA integrado.' },
    { q: 'A plataforma e compativel com quais drones?', a: 'Atualmente oferecemos integracao nativa com drones DJI (Mavic 3E/3T, Matrice 30, Matrice 3TD) via DJI Cloud API. Outros fabricantes podem ser integrados via MQTT.' },
    { q: 'Como funciona o controle de conformidade ANAC/DECEA?', a: 'O sistema monitora automaticamente documentacao de drones, licencas de pilotos e prazos regulatorios. Alertas sao enviados 30 dias antes do vencimento e anomalias sao reportadas em tempo real.' },
    { q: 'Posso gerar relatorios automaticamente?', a: 'Sim. O assistente IA Jorginho pode gerar relatorios de jornada diarios ou mensais em PDF, Excel ou Word por comando. Tambem e possivel gerar relatorios manualmente pela interface.' },
    { q: 'A plataforma funciona em dispositivos moveis?', a: 'Sim. A plataforma e totalmente responsiva e funciona em smartphones, tablets e desktops. Tambem oferecemos um modulo PWA otimizado para controladores Smart Controller.' },
  ];

  const [activeService, setActiveService] = useState(0);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="landing-page">
      {/* ========== HEADER ========== */}
      <header className="fixed top-0 w-full z-50 bg-[#0A1628]/95 backdrop-blur-md border-b border-white/10" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollTo('hero')}>
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#C8941A] to-amber-600 flex items-center justify-center">
              <Plane size={18} className="text-white" />
            </div>
            <span className="text-white font-bold text-lg tracking-tight">Gestao de RPAS</span>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {[['Solucoes', 'features'], ['Servicos', 'services'], ['Metricas', 'metrics'], ['FAQ', 'faq'], ['Contato', 'contact']].map(([label, id]) => (
              <button key={id} onClick={() => scrollTo(id)} className="text-gray-300 hover:text-[#C8941A] text-sm font-medium transition-colors">{label}</button>
            ))}
            <Button onClick={() => setShowLogin(true)} className="bg-[#C8941A] hover:bg-[#b0831a] text-white text-sm h-9 px-5 rounded-lg font-semibold" data-testid="header-login-btn">
              ACESSAR
            </Button>
          </nav>

          {/* Mobile Menu Toggle */}
          <button className="md:hidden text-white" onClick={() => setMobileMenu(!mobileMenu)}>
            {mobileMenu ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileMenu && (
          <div className="md:hidden bg-[#0A1628] border-t border-white/10 px-4 py-4 space-y-3">
            {[['Solucoes', 'features'], ['Servicos', 'services'], ['Metricas', 'metrics'], ['FAQ', 'faq'], ['Contato', 'contact']].map(([label, id]) => (
              <button key={id} onClick={() => scrollTo(id)} className="block text-gray-300 hover:text-[#C8941A] text-sm font-medium w-full text-left py-2">{label}</button>
            ))}
            <Button onClick={() => { setShowLogin(true); setMobileMenu(false); }} className="bg-[#C8941A] text-white text-sm w-full mt-2">ACESSAR</Button>
          </div>
        )}
      </header>

      {/* ========== HERO ========== */}
      <section id="hero" className="relative min-h-screen flex items-center pt-16 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#1E3A5F] to-[#0A1628]" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(200,148,26,0.3) 1px, transparent 0)', backgroundSize: '40px 40px' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#C8941A]/15 border border-[#C8941A]/30 text-[#C8941A] text-xs font-semibold tracking-wider animate-fade-in">
                <Shield size={12} /> CONFORMIDADE ANAC/DECEA
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] animate-fade-in" style={{ animationDelay: '0.1s' }}>
                Inteligencia e controle para operacoes{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#C8941A] to-amber-400">RPAS</span>{' '}
                de excelencia
              </h1>

              <p className="text-gray-400 text-base sm:text-lg max-w-lg leading-relaxed animate-fade-in" style={{ animationDelay: '0.2s' }}>
                Plataforma profissional de gestao e monitoramento de drones. Telemetria em tempo real, relatorios automatizados e compliance regulatorio em um unico sistema.
              </p>

              <div className="flex flex-wrap gap-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <Button onClick={() => setShowLogin(true)} className="bg-[#C8941A] hover:bg-[#b0831a] text-white h-12 px-8 rounded-xl text-sm font-bold shadow-lg shadow-[#C8941A]/20 hover:shadow-[#C8941A]/40 transition-all hover:-translate-y-0.5" data-testid="hero-cta-primary">
                  ACESSAR PLATAFORMA <ArrowRight size={16} className="ml-2" />
                </Button>
                <Button onClick={() => scrollTo('contact')} variant="outline" className="border-white/20 text-white hover:bg-white/10 h-12 px-8 rounded-xl text-sm font-bold" data-testid="hero-cta-secondary">
                  SOLICITAR DEMO
                </Button>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-3 pt-4 animate-fade-in" style={{ animationDelay: '0.4s' }}>
                {['ANAC Certificado', 'DECEA Autorizado', 'ICA 100-40'].map(badge => (
                  <span key={badge} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-gray-400 text-[10px] font-semibold tracking-wider">
                    <CheckCircle2 size={10} className="text-emerald-400" /> {badge}
                  </span>
                ))}
              </div>
            </div>

            {/* Hero Video */}
            <div className="relative animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50">
                <video src={VIDEO_URL} autoPlay loop muted playsInline className="w-full h-auto" />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full bg-[#C8941A]/10 blur-3xl" />
              <div className="absolute -top-4 -left-4 w-32 h-32 rounded-full bg-blue-500/10 blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* ========== METRICS COUNTERS ========== */}
      <section id="metrics" className="py-16 bg-[#0A1628] border-t border-white/5" data-testid="metrics-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { ref: missions.ref, count: missions.count, suffix: '+', label: 'Missoes Realizadas', icon: Plane },
              { ref: hours.ref, count: hours.count, suffix: 'h', label: 'Horas de Voo', icon: Clock },
              { ref: drones.ref, count: drones.count, suffix: '+', label: 'Aeronaves Monitoradas', icon: Radio },
              { ref: clients.ref, count: clients.count, suffix: '+', label: 'Clientes Ativos', icon: User },
            ].map((m, i) => (
              <div key={i} ref={m.ref} className="text-center group" data-testid={`counter-${i}`}>
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-[#C8941A]/10 border border-[#C8941A]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <m.icon size={20} className="text-[#C8941A]" />
                </div>
                <div className="text-3xl sm:text-4xl font-extrabold text-white">{m.count.toLocaleString()}{m.suffix}</div>
                <div className="text-gray-500 text-xs font-medium mt-1 tracking-wider uppercase">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FEATURES ========== */}
      <section id="features" className="py-20 bg-[#F4F6F9]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-base sm:text-lg font-bold text-[#C8941A] uppercase tracking-wider mb-3">Por que Gestao de RPAS?</h2>
            <p className="text-[#0A1628] text-2xl sm:text-3xl font-bold max-w-2xl mx-auto">Tecnologia de ponta para operacoes aereas profissionais</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-[#C8941A]/40 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group" data-testid={`feature-card-${i}`}>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <f.icon size={22} className="text-white" />
                </div>
                <h3 className="text-[#0A1628] font-bold text-sm mb-2">{f.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== SERVICES TABS ========== */}
      <section id="services" className="py-20 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-base sm:text-lg font-bold text-[#C8941A] uppercase tracking-wider mb-3">Nossos Servicos</h2>
            <p className="text-[#0A1628] text-2xl sm:text-3xl font-bold">Solucoes completas para sua operacao</p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {services.map((s, i) => (
              <button key={i} onClick={() => setActiveService(i)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${activeService === i ? 'bg-[#0A1628] text-[#C8941A] shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                data-testid={`service-tab-${i}`}
              >
                <s.icon size={16} /> {s.title}
              </button>
            ))}
          </div>

          <div className="bg-gradient-to-br from-[#0A1628] to-[#1E3A5F] rounded-2xl p-8 md:p-12 text-white">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">{services[activeService].title}</h3>
                <div className="space-y-3">
                  {services[activeService].items.map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle2 size={16} className="text-[#C8941A] flex-shrink-0" />
                      <span className="text-gray-300 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <Button onClick={() => scrollTo('contact')} className="mt-6 bg-[#C8941A] hover:bg-[#b0831a] text-white rounded-xl px-6 h-10 text-sm font-semibold">
                  Solicitar Demo <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
              <div className="relative">
                <div className="bg-white/5 rounded-xl border border-white/10 p-6 backdrop-blur-sm" data-testid="mock-dashboard">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-emerald-400 text-xs font-mono">SISTEMA ATIVO</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Drones Online', val: '3/4', color: 'text-emerald-400' },
                      { label: 'Missoes Hoje', val: '12', color: 'text-[#C8941A]' },
                      { label: 'Pilotos Ativos', val: '8', color: 'text-blue-400' },
                      { label: 'Alertas', val: '0', color: 'text-gray-400' },
                    ].map((s, i) => (
                      <div key={i} className="bg-white/5 rounded-lg p-3 border border-white/5">
                        <div className="text-gray-500 text-[10px] font-mono uppercase">{s.label}</div>
                        <div className={`text-xl font-bold ${s.color}`}>{s.val}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FAQ ========== */}
      <section id="faq" className="py-20 bg-[#F4F6F9]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-base sm:text-lg font-bold text-[#C8941A] uppercase tracking-wider mb-3">Perguntas Frequentes</h2>
            <p className="text-[#0A1628] text-2xl sm:text-3xl font-bold">Tire suas duvidas</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <FAQItem key={i} question={faq.q} answer={faq.a} isOpen={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? null : i)} />
            ))}
          </div>
        </div>
      </section>

      {/* ========== CONTACT ========== */}
      <section id="contact" className="py-20 bg-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-base sm:text-lg font-bold text-[#C8941A] uppercase tracking-wider mb-3">Contato</h2>
              <p className="text-[#0A1628] text-2xl sm:text-3xl font-bold mb-6">Solicite uma demonstracao</p>
              <p className="text-gray-500 text-sm leading-relaxed mb-8">Entre em contato para conhecer a plataforma e descobrir como podemos otimizar suas operacoes RPAS.</p>

              <div className="space-y-4">
                {[
                  { icon: Mail, text: 'contato@gestaoderpas.com.br' },
                  { icon: Phone, text: '+55 (71) 98355-9776' },
                  { icon: MapPin, text: 'Salvador, BA - Brasil' },
                ].map((c, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#0A1628] flex items-center justify-center">
                      <c.icon size={16} className="text-[#C8941A]" />
                    </div>
                    <span className="text-gray-600 text-sm">{c.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#F4F6F9] rounded-2xl p-8 border border-gray-200">
              {contactSent ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 size={28} className="text-emerald-600" />
                  </div>
                  <h3 className="text-[#0A1628] font-bold text-lg mb-2">Mensagem Enviada!</h3>
                  <p className="text-gray-500 text-sm">Entraremos em contato em breve.</p>
                </div>
              ) : (
                <form onSubmit={handleContact} className="space-y-4" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[#0A1628] text-xs font-semibold uppercase tracking-wider block mb-1.5">Nome</label>
                      <Input value={contactForm.nome} onChange={e => setContactForm({...contactForm, nome: e.target.value})} placeholder="Seu nome" required className="h-10 rounded-xl border-gray-300 text-sm" />
                    </div>
                    <div>
                      <label className="text-[#0A1628] text-xs font-semibold uppercase tracking-wider block mb-1.5">Email</label>
                      <Input type="email" value={contactForm.email} onChange={e => setContactForm({...contactForm, email: e.target.value})} placeholder="seu@email.com" required className="h-10 rounded-xl border-gray-300 text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[#0A1628] text-xs font-semibold uppercase tracking-wider block mb-1.5">Empresa</label>
                    <Input value={contactForm.empresa} onChange={e => setContactForm({...contactForm, empresa: e.target.value})} placeholder="Nome da empresa" className="h-10 rounded-xl border-gray-300 text-sm" />
                  </div>
                  <div>
                    <label className="text-[#0A1628] text-xs font-semibold uppercase tracking-wider block mb-1.5">Mensagem</label>
                    <textarea value={contactForm.mensagem} onChange={e => setContactForm({...contactForm, mensagem: e.target.value})} placeholder="Descreva sua necessidade..." required rows={4} className="w-full rounded-xl border border-gray-300 p-3 text-sm resize-none focus:border-[#C8941A] focus:ring-1 focus:ring-[#C8941A]/20 outline-none" />
                  </div>
                  <Button type="submit" className="w-full bg-[#C8941A] hover:bg-[#b0831a] text-white h-11 rounded-xl font-semibold text-sm" data-testid="contact-submit-btn">
                    <Send size={14} className="mr-2" /> ENVIAR MENSAGEM
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="bg-[#0A1628] border-t border-white/5 py-12" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#C8941A] to-amber-600 flex items-center justify-center">
                  <Plane size={18} className="text-white" />
                </div>
                <span className="text-white font-bold text-lg">Gestao de RPAS</span>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed">Inteligencia e controle para operacoes RPAS de excelencia. Plataforma completa de gestao e monitoramento profissional.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Links</h4>
              <div className="space-y-2">
                {[['Solucoes', 'features'], ['Servicos', 'services'], ['FAQ', 'faq'], ['Contato', 'contact']].map(([label, id]) => (
                  <button key={id} onClick={() => scrollTo(id)} className="block text-gray-500 hover:text-[#C8941A] text-xs transition-colors">{label}</button>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Contato</h4>
              <div className="space-y-2 text-gray-500 text-xs">
                <p>contato@gestaoderpas.com.br</p>
                <p>+55 (71) 98355-9776</p>
                <p>Salvador, BA - Brasil</p>
              </div>
            </div>
          </div>
          <div className="border-t border-white/5 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-600 text-xs">Gestao de RPAS - Todos os direitos reservados</p>
            <p className="text-gray-600 text-xs">monitoramentorpas.com</p>
          </div>
        </div>
      </footer>

      {/* ========== JORGINHO SALES CHATBOT ========== */}
      {chatOpen && (
        <div className="fixed bottom-20 right-4 z-50 w-[380px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden animate-scale-in" style={{ height: '520px' }} data-testid="sales-chatbot">
          {/* Chat Header */}
          <div className="bg-[#0A1628] px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#C8941A] to-amber-600 flex items-center justify-center">
                <Bot size={16} className="text-white" />
              </div>
              <div>
                <p className="text-white text-sm font-bold">Jorginho</p>
                <p className="text-gray-400 text-[9px] font-mono">Consultor Senior RPAS</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => { setAutoVoice(v => !v); if (chatAudioRef.current) { chatAudioRef.current.pause(); chatAudioRef.current = null; setChatPlaying(null); } }}
                className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${autoVoice ? 'bg-[#C8941A]/20 text-[#C8941A]' : 'bg-white/10 text-gray-500'}`}
                title={autoVoice ? 'Voz ativada' : 'Voz desativada'}
                data-testid="sales-auto-voice"
              >
                <Volume2 size={13} />
              </button>
              <button onClick={() => setChatOpen(false)} className="w-7 h-7 rounded-lg bg-white/10 text-gray-400 hover:text-white flex items-center justify-center transition-colors">
                <Minimize2 size={13} />
              </button>
            </div>
          </div>

          {/* Chat Messages */}
          <div ref={chatScrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50">
            {chatMessages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className="max-w-[85%]">
                  <div className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[#0A1628] text-white rounded-br-md'
                      : 'bg-white border border-gray-200 text-gray-700 shadow-sm rounded-bl-md'
                  }`}>
                    {msg.role === 'user' ? msg.content : (
                      <ReactMarkdown components={{
                        p: ({children}) => <p className="mb-1.5 last:mb-0">{children}</p>,
                        strong: ({children}) => <strong className="font-semibold text-[#0A1628]">{children}</strong>,
                        ul: ({children}) => <ul className="list-disc list-inside mb-1.5 space-y-0.5 text-xs">{children}</ul>,
                        li: ({children}) => <li className="text-xs">{children}</li>,
                      }}>{msg.content}</ReactMarkdown>
                    )}
                  </div>
                  {msg.role === 'assistant' && msg.content && (
                    <button
                      onClick={() => speakResponse(msg.content, i)}
                      disabled={chatTtsLoading === i}
                      className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-mono transition-colors ${
                        chatPlaying === i ? 'bg-[#C8941A]/20 text-[#C8941A]' : 'bg-gray-100 text-gray-400 hover:text-[#C8941A]'
                      }`}
                      data-testid={`sales-tts-${i}`}
                    >
                      {chatTtsLoading === i ? <Loader2 size={8} className="animate-spin" /> : chatPlaying === i ? <Square size={8} /> : <Volume2 size={8} />}
                      {chatTtsLoading === i ? 'GERANDO...' : chatPlaying === i ? 'PARAR' : 'OUVIR'}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-2.5 shadow-sm">
                  <div className="flex items-center gap-2">
                    <Loader2 size={14} className="text-[#C8941A] animate-spin" />
                    <span className="text-gray-400 text-[10px] font-mono">Jorginho pensando...</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Prompts */}
          {chatMessages.length <= 1 && (
            <div className="px-3 py-2 border-t border-gray-100 bg-white flex gap-1.5 overflow-x-auto flex-shrink-0">
              {['Quero otimizar minha operacao', 'Quanto custa?', 'Como funciona a gestao?', 'Agendar diagnostico'].map(q => (
                <button key={q} onClick={() => sendChatMessage(q)} className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-[10px] font-medium whitespace-nowrap hover:bg-[#C8941A]/10 hover:text-[#C8941A] transition-colors">{q}</button>
              ))}
            </div>
          )}

          {/* Chat Input with Mic */}
          <form onSubmit={(e) => { e.preventDefault(); sendChatMessage(); }} className="px-3 py-2.5 border-t border-gray-200 bg-white flex items-center gap-1.5 flex-shrink-0">
            <button
              type="button"
              onClick={toggleMic}
              className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${
                isRecording
                  ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-500/30'
                  : 'bg-gray-100 text-gray-500 hover:bg-[#C8941A]/10 hover:text-[#C8941A]'
              }`}
              data-testid="sales-mic-btn"
            >
              {isRecording ? <MicOff size={15} /> : <Mic size={15} />}
            </button>
            <input
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              placeholder={isRecording ? 'Gravando...' : 'Digite ou fale...'}
              className="flex-1 h-9 rounded-xl border border-gray-300 px-3 text-sm focus:border-[#C8941A] focus:ring-1 focus:ring-[#C8941A]/20 outline-none"
              disabled={chatLoading || isRecording}
              data-testid="sales-chat-input"
            />
            <button type="submit" disabled={!chatInput.trim() || chatLoading} className="w-9 h-9 rounded-xl bg-[#C8941A] hover:bg-[#b0831a] text-white flex items-center justify-center disabled:opacity-40 transition-colors flex-shrink-0" data-testid="sales-chat-send">
              <Send size={14} />
            </button>
          </form>
        </div>
      )}

      {/* Chat Toggle Button */}
      {!chatOpen && (
        <button
          onClick={() => setChatOpen(true)}
          className="fixed bottom-36 right-4 z-40 flex items-center gap-2 h-12 pl-3 pr-4 rounded-full bg-[#0A1628] shadow-lg shadow-black/20 hover:scale-105 transition-transform"
          data-testid="sales-chat-toggle"
        >
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C8941A] to-amber-600 flex items-center justify-center">
            <Bot size={16} className="text-white" />
          </div>
          <span className="text-white text-xs font-bold tracking-wider">RPAS IA</span>
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#C8941A] border-2 border-white animate-pulse" />
        </button>
      )}

      {/* ========== WHATSAPP BUTTON ========== */}
      <a href="https://wa.me/5571983559776?text=Olá! Gostaria de saber mais sobre a plataforma Gestão de RPAS."
        target="_blank" rel="noopener noreferrer"
        className="fixed bottom-20 right-4 z-40 w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-lg shadow-[#25D366]/30 hover:scale-110 transition-transform"
        data-testid="whatsapp-btn"
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      </a>

      {/* ========== LOGIN DIALOG ========== */}
      <Dialog open={showLogin} onOpenChange={setShowLogin}>
        <DialogContent className="bg-white border border-gray-200 max-w-md p-0 overflow-hidden rounded-2xl">
          <DialogHeader className="sr-only"><DialogTitle>Login</DialogTitle></DialogHeader>
          <div className="bg-[#1f2a36] w-full">
            <video src={VIDEO_URL} autoPlay loop muted playsInline className="w-full h-auto" data-testid="login-logo" />
          </div>
          <div className="p-8">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-[#0A1628] uppercase tracking-wider mb-1">Acesso ao Sistema</h2>
              <p className="text-gray-500 text-xs font-medium">GESTAO DE RPAS | MONITORAMENTO PROFISSIONAL</p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
                <span className="text-red-600 text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-[#0A1628] text-xs font-semibold uppercase tracking-wider">Usuario</Label>
                <div className="relative mt-1.5">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input value={username} onChange={e => setUsername(e.target.value)} className="pl-10 h-11 rounded-xl border-gray-300 text-sm" placeholder="Digite seu usuario" required data-testid="username-input" />
                </div>
              </div>
              <div>
                <Label className="text-[#0A1628] text-xs font-semibold uppercase tracking-wider">Senha</Label>
                <div className="relative mt-1.5">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className="pl-10 pr-10 h-11 rounded-xl border-gray-300 text-sm" placeholder="Digite sua senha" required data-testid="password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#0A1628]">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#C8941A] hover:bg-[#b0831a] text-white h-11 rounded-xl font-bold text-sm" data-testid="login-submit-btn">
                {loading ? <div className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span>AUTENTICANDO...</span></div> : 'ACESSAR SISTEMA'}
              </Button>
            </form>

            <div className="mt-6 pt-4 border-t border-gray-200 text-center">
              <p className="text-[#0A1628] text-xs font-semibold">monitoramentorpas.com</p>
              <p className="text-gray-400 text-[10px] mt-1">Gestao de RPAS - Todos os direitos reservados</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
